import React, { useState } from "react";

import { Menu as MenuIcon } from "@mui/icons-material";
import { List, ListItem, MenuItem } from "@mui/material";
import { useAppState } from "Context/StateContext";
import { useViewport } from "Hooks/useViewPort";
import { Sms as IconSMS, Call as IconCall, Mobile as IconMobile } from "iconsax-react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import deleteAllTokens from "Util/deleteAllTokens";
import { LINKS } from "../../../../../../constants";

import useStyles, {
  MenuIconWrapper,
  StyledMenu,
  UserInfos,
  UserMenuItem,
  Divider,
  DrawerTitle,
  StyledSwipeableDrawer,
  Subtitle,
  DrawerUserInfosContainer,
  ItemInfoWrapperJustified,
  PrimaryText,
  SecondaryText,
  ThirdText,
  LogOutText,
  StyledToolbar,
  UserMenuIcon,
  UserMenuIconImage,
  UserAvatarContainer,
  IconAndSecondaryText,
} from "./styles";

const BREAKPOINT = 768;

// const ARROW_ICON = require("../../../../../../assets/icons/arrow-down.svg");
const CLOSE_ICON = require("Assets/icons/close.svg");
const LANGUAGE_ICON = require("Assets/icons/language.svg");
const LOGOUT_ICON = require("Assets/icons/logout.svg");
const TERMS_ICON = require("Assets/icons/terms.svg");

export const Menu = () => {
  const [userDropdownMenu, setUserDropdownMenu] = useState<null | HTMLElement>(null);
  const [swipeableIsOpen, setSwipeableIsOpen] = useState<boolean>(false);

  const { t: translate } = useTranslation();
  const history = useHistory();
  const { width } = useViewport();
  const classes = useStyles();
  const { currentCustomerUser, logoutCustomerUser } = useAppState();

  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setUserDropdownMenu(event.currentTarget);
  };

  const handleClose = () => {
    setUserDropdownMenu(null);
  };

  const toggleDrawer = () => {
    setSwipeableIsOpen(!swipeableIsOpen);
  };

  const openDrawer = () => {
    setSwipeableIsOpen(true);
  };

  const closeDrawer = () => {
    setSwipeableIsOpen(false);
  };

  const getCurrentUserAvatar = (): string => {
    // eslint-disable-next-line global-require
    return require("Assets/profile.png");
  };

  const openUrl = (url: string, external = false) => {
    if (external) {
      window.open(url, "_blank");
    } else {
      history.push(`/app/${url}`);
    }
    handleClose();
  };

  const renderLanguages = (isSwipeable?: boolean) => {
    return (
      <ItemInfoWrapperJustified isSwipeable={isSwipeable} onClick={() => {}} disabled>
        <div className={classes.centerAlign}>
          <img src={LANGUAGE_ICON} alt="translate language icon" />
          {translate("english")}
        </div>
        {/* <img src={ARROW_ICON} alt="menu arrow down icon" /> */}
      </ItemInfoWrapperJustified>
    );
  };

  const renderMenuItems = (isSwipeable?: boolean) => {
    return [{ menuTitle: translate("termsOfUse"), href: LINKS.termsOfUse }].map(
      (navBarLinkToShow) => {
        return (
          <MenuItem
            data-cy={navBarLinkToShow?.menuTitle.toLowerCase()}
            key={navBarLinkToShow?.menuTitle}
            className={`${classes.menuItem} ${isSwipeable ? classes.menuItemSwipeable : ""}`}
            onClick={() => openUrl(navBarLinkToShow?.href, true)}
          >
            <img src={TERMS_ICON} alt="terms of use icon" />

            {translate(navBarLinkToShow?.menuTitle)}
          </MenuItem>
        );
      }
    );
  };

  const renderListItemLogout = (isSwipeable?: boolean) => {
    return (
      <ListItem
        button
        onClick={logoutCustomerUser}
        className={`${classes.logOutItem} ${isSwipeable ? classes.logOutItemSwipeable : ""}`}
      >
        <img src={LOGOUT_ICON} alt="logout icon" />
        <LogOutText primary={translate("logout")} />
      </ListItem>
    );
  };

  const renderUserAvatar = () => {
    return (
      <UserMenuItem disableRipple disableGutters disableTouchRipple>
        <UserAvatarContainer>
          <UserMenuIcon>
            <UserMenuIconImage src={getCurrentUserAvatar()} alt="user profile" />
          </UserMenuIcon>
        </UserAvatarContainer>
        <UserInfos>
          <PrimaryText>
            {`${translate("hello")} ${currentCustomerUser?.fullName}!` || translate("unknown")}
          </PrimaryText>
          {currentCustomerUser?.email && (
            <IconAndSecondaryText>
              <IconSMS size={14} />
              <SecondaryText>{currentCustomerUser?.email || translate("unknown")}</SecondaryText>
            </IconAndSecondaryText>
          )}
          {currentCustomerUser?.phone && (
            <IconAndSecondaryText>
              <IconCall size={14} />
              <SecondaryText>{currentCustomerUser?.phone || translate("unknown")}</SecondaryText>
            </IconAndSecondaryText>
          )}
          {currentCustomerUser?.mobile && (
            <IconAndSecondaryText>
              <IconMobile size={14} />
              <SecondaryText>{currentCustomerUser?.mobile || translate("unknown")}</SecondaryText>
            </IconAndSecondaryText>
          )}
        </UserInfos>
      </UserMenuItem>
    );
  };

  const renderDivider = () => {
    return <Divider />;
  };

  const renderDrawerUserInfos = () => {
    return (
      <DrawerUserInfosContainer>
        <Subtitle>{translate("logedInAs")}</Subtitle>
        <UserInfos>
          <SecondaryText>{currentCustomerUser?.fullName || translate("unknown")}</SecondaryText>
          {currentCustomerUser?.email && (
            <ThirdText>{currentCustomerUser?.email || translate("unknown")}</ThirdText>
          )}
        </UserInfos>
      </DrawerUserInfosContainer>
    );
  };

  const renderDrawerHeader = () => {
    return (
      <div className={classes.drawerHeaderContainer}>
        <div className={classes.drawerHeader}>
          <UserMenuIcon onClick={closeDrawer}>
            <img src={CLOSE_ICON} alt="close drawer icon" />
          </UserMenuIcon>
          <DrawerTitle>{translate("menu")}</DrawerTitle>
        </div>
        {renderDrawerUserInfos()}
      </div>
    );
  };

  const renderMenu = () => {
    if (BREAKPOINT < width) {
      return (
        <StyledMenu
          anchorEl={userDropdownMenu}
          keepMounted={false}
          open={Boolean(userDropdownMenu)}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          PaperProps={{ className: classes.paperProps }}
        >
          {renderUserAvatar()}
          {renderDivider()}
          {renderLanguages(false)}
          {renderMenuItems(false)}
          {renderDivider()}
          {renderListItemLogout(false)}
        </StyledMenu>
      );
    }

    return (
      <StyledSwipeableDrawer
        anchor="right"
        open={swipeableIsOpen}
        onClose={closeDrawer}
        onOpen={openDrawer}
        SwipeAreaProps={{ className: classes.swipeArea }}
      >
        <List component="nav">
          {renderDrawerHeader()}
          {renderDivider()}
          {renderLanguages(true)}
          {renderMenuItems(true)}
          {renderDivider()}
          {renderListItemLogout(true)}
          {renderDivider()}
        </List>
      </StyledSwipeableDrawer>
    );
  };

  return (
    <StyledToolbar>
      <MenuIconWrapper onClick={BREAKPOINT < width ? handleClick : toggleDrawer}>
        <MenuIcon htmlColor="#15375C" />
      </MenuIconWrapper>
      {renderMenu()}
    </StyledToolbar>
  );
};
